import {
  mutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import type {
  CreditCardWriteResponse,
  UpdateCreditCardBody,
} from 'src/queries/creditCards/network/creditCardWrites.models'
import { putCreditCard } from 'src/queries/creditCards/network/creditCardWrites.network'
import type { QueryError } from 'src/queries/network.utils'
import { creditCardsQueryKeys } from 'src/queries/queries.constants'

/**
 * `PUT /creditcards/{id}` — kept in its own LEAF module.
 *
 * Its siblings in `creditCardWrites.queries.tsx` dispatch Redux lifecycle
 * actions and open toasts, so that module transitively imports `app/Toast`,
 * which reaches the Redux store's own initialization graph. Any feature that
 * imported the update hook from there closed an import cycle and left reselect
 * selectors undefined at module-eval time — a whole jest suite failing on an
 * unrelated-looking `createSelector expects all input-selectors to be
 * functions` (see `src/CLAUDE.md`, "Store-initialization import cycles").
 *
 * Update alone owns no Redux lifecycle and no toast, so it can live here
 * importing nothing but the network function and the key factory.
 * `creditCardWrites.queries.tsx` re-exports both symbols, so existing call
 * sites are unchanged.
 */

export type UpdateCreditCardVariables = {
  creditCardId: string
  body: UpdateCreditCardBody
}

export const creditCardUpdateMutations = {
  update: () =>
    mutationOptions<
      CreditCardWriteResponse,
      QueryError,
      UpdateCreditCardVariables
    >({
      mutationFn: ({ creditCardId, body }) => putCreditCard(creditCardId, body),
    }),
}

/**
 * Update a credit card (`PUT /creditcards/{id}`) — the TanStack Query path for
 * NEW server-state code per EX-39745. Unlike the legacy `putCreditCard` thunk
 * (still used by the existing Account Settings boxes until their migration
 * slice), this owns no Redux lifecycle and no effect-driven refetch: the
 * card's detail key is invalidated in `onSuccess`, so consumers re-read the
 * mutated card straight from the cache.
 *
 * Invalidation is deliberately NARROW — the single `creditcards` detail key
 * for the mutated card, never the family root. `exact` matters here:
 * `creditCardsQueryKeys.permissions(id)` extends `detail(id)`, so the default
 * prefix match would sweep permissions in too.
 */
export const useUpdateCreditCard = () => {
  const queryClient = useQueryClient()

  return useMutation<
    CreditCardWriteResponse,
    QueryError,
    UpdateCreditCardVariables
  >({
    ...creditCardUpdateMutations.update(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: creditCardsQueryKeys.detail(data.creditCard.id),
        exact: true,
      })
    },
  })
}
